import { computed, Ref, watch } from 'vue';

import ModalSpinner from '@/components/modal/ModalSpinner.vue';
import {
  getMerchantDataSolidPaymentForm,
  GooglePayBtnType,
  initSolidForm,
  PaypalBtnLabel,
} from '@/components/payment/components/payment-method/components/solid/solid-payment-method/helpers/self/self';
import { usePaymentForm } from '@/components/payment/components/payment-method/components/solid/solid-payment-method/hooks/use-payment-form/use-payment-form';
import { memoizeClear } from '@/helpers/memoize/memoize';
import { getUniqNumber } from '@/helpers/number';
import { trackPaymentFlow } from '@/helpers/track/track-payment/track-payment';
import { PaymentStatePackage } from '@/pages/payment/store/payment-state';
import { bq } from '@/services/bq/bq';
import { useI18n } from '@/services/i18n';
import { useModal } from '@/services/modal/modal';
import { getState } from '@/store/store-helper';

import { messages } from './i18n';

const { t } = useI18n({ messages });

type UseSelfArg = {
  pkg: Ref<PaymentStatePackage>;
  isCardEnabled: boolean;
  isGooglePayEnabled: boolean;
  isApplePayEnabled: boolean;
  isAuthorizeCardToken: boolean;
  isPaypalButtonParamsEnabled: boolean;
  googlePayBtnType: GooglePayBtnType;
  paypalBtnLabel: PaypalBtnLabel;
};

export function useSelf(arg: UseSelfArg) {
  // Ref -----------------------------------------------------------------------
  let destroySolidForm: () => void;
  const solidCardElId = 'mma-payment-form-container' + getUniqNumber();
  const solidGooglePayElId = 'mma-google-pay-container' + getUniqNumber();
  const solidApplePayElId = 'mma-apple-pay-container' + getUniqNumber();
  const solidPayPalElId = 'mma-paypal-container' + getUniqNumber();
  let timeStartLoadingMs: number = 0;

  // Hook ----------------------------------------------------------------------
  const spinnerModal = useModal(() => ({
    component: ModalSpinner,
  }));

  // Computed ------------------------------------------------------------------
  const isGooglePayAvailable = computed<boolean>(() => {
    return getState().payment.paymentMethods.googlePay.isAvailable;
  });

  const isApplePayAvailable = computed<boolean>(() => {
    return getState().payment.paymentMethods.applePay.isAvailable;
  });

  // Method --------------------------------------------------------------------
  function destroy() {
    destroySolidForm?.();
    memoizeClear(getMerchantDataSolidPaymentForm);
  }

  // Reload solid form if user select another package
  function watchPkgId() {
    watch(
      () => arg.pkg.value.id,
      () => {
        loadSolidForm();
      },
    );
  }

  async function loadSolidForm() {
    destroySolidForm?.();

    destroySolidForm = (
      await initSolidForm({
        pkg: arg.pkg.value,
        isCardEnabled: arg.isCardEnabled,
        isGooglePayEnabled: arg.isGooglePayEnabled && isGooglePayAvailable.value,
        isApplePayEnabled: arg.isApplePayEnabled && isApplePayAvailable.value,
        isAuthorizeCardToken: arg.isAuthorizeCardToken,
        btnSubmitText: t('submit'),
        containerId: solidCardElId,
        googlePayContainerId: solidGooglePayElId,
        applePayContainerId: solidApplePayElId,
        googlePayBtnType: arg.googlePayBtnType,
        payPalContainerId: solidPayPalElId,
        isPaypalButtonParamsEnabled: arg.isPaypalButtonParamsEnabled,
        paypalBtnLabel: arg.paypalBtnLabel,
        handleFormMounted(data) {
          spinnerModal.hideModal();

          switch (data.entity) {
            case 'form':
              usePaymentForm().setFormLoaded();
              break;
            case 'applebtn':
              usePaymentForm().setApplePayLoaded();
              break;
            case 'googlebtn':
              usePaymentForm().setGooglePayLoaded();
              break;
          }

          bq.track('form_finish_loading', {
            entity: data.entity,
            timeDuration: Date.now() - timeStartLoadingMs,
          });
        },
      })
    ).destroyForm;
  }

  async function init() {
    spinnerModal.showModal();
    timeStartLoadingMs = Date.now();
    bq.track('form_start_loading', {
      timeStart: timeStartLoadingMs,
    });

    await loadSolidForm();
    watchPkgId();

    trackPaymentFlow({
      step: 'solid-form',
    });
  }

  return {
    solidCardElId,
    solidGooglePayElId,
    solidApplePayElId,
    solidPayPalElId,
    isGooglePayAvailable,
    isApplePayAvailable,
    destroy,
    init,
  };
}
